import type {
	IkeAction,
	IkeActionDepartmentAdminList,
	IkeActionDepartmentList,
	IkeActionDepartmentWriteableList,
	IkeActionDeviceGetDeviceId,
	IkeActionEventBatch,
	IkeActionEventExtract,
	IkeActionEventProcess,
	IkeActionOrgGetByOrgId,
	IkeActionSetSettings,
	IkeActionUserIPFDatabases,
	IkeActionUserMe,
	IkeActionVersionCheck,
	DepartmentJSONObject,
	UserJSONObject,
} from '@ikegps/core-api';
import type { Log } from '../log';
import { IkeFetchableMany, IkeFetchableOne, IkeFetchableStore } from './fetchable';
import { IkeRestObjects } from './objects/object';
import type { IkeRest } from './rest';
import { IkeRestDepartment } from './rest.department';

export class IkeRestUser {
	Objects: IkeRestObjects = IkeRestObjects;

	me: IkeFetchableOne<UserJSONObject>;
	departments: IkeFetchableMany<DepartmentJSONObject>;
	adminDepartments: IkeFetchableMany<DepartmentJSONObject>;
	writeableDepartments: IkeFetchableMany<DepartmentJSONObject>;

	core: IkeRest;
	accessToken?: string;
	log: Log;

	boundDepartmentId: string;
	boundDepartmentRest: IkeRestDepartment;

	User = {
		Settings: {
			change: this.bindAction<IkeActionSetSettings>('user.settings.set'),
		},
		me: this.bindAction<IkeActionUserMe>('user.me'),
		ipfDatabases: this.bindAction<IkeActionUserIPFDatabases>('user.ipf.databases'),
	};

	Version = {
		check: this.bindAction<IkeActionVersionCheck>('version.check'),
	};

	Department = {
		list: this.bindAction<IkeActionDepartmentList>('department.list'),
		adminList: this.bindAction<IkeActionDepartmentAdminList>('department.admin.list'),
		writeableList: this.bindAction<IkeActionDepartmentWriteableList>('department.writeable.list'),
	};

	Org = {
		getById: this.bindAction<IkeActionOrgGetByOrgId>('org.get.orgid'),
	};

	Device = {
		getByDeviceId: this.bindAction<IkeActionDeviceGetDeviceId>('device.get.deviceid'),
	};

	Event = {
		batch: this.bindAction<IkeActionEventBatch>('event.batch'),
		extract: this.bindAction<IkeActionEventExtract>('event.extract'),
		process: this.bindAction<IkeActionEventProcess>('event.process'),
	};

	constructor(core: IkeRest, accessToken?: string) {
		this.core = core;

		this.log = core.log.child({ accessToken });
		this.accessToken = accessToken;

		this.me = new IkeFetchableOne(this.log, new IkeFetchableStore(), () => this.User.me());
		this.departments = new IkeFetchableMany(this.log, new IkeFetchableStore(), () =>
			this.Department.list().then((c) => c.results)
		);
		this.adminDepartments = new IkeFetchableMany(this.log, new IkeFetchableStore(), () =>
			this.Department.adminList().then((c) => c.results)
		);
		this.writeableDepartments = new IkeFetchableMany(this.log, new IkeFetchableStore(), () =>
			this.Department.writeableList().then((c) => c.results)
		);
	}

	bind(departmentId: string) {
		if (this.boundDepartmentId !== departmentId) {
			this.boundDepartmentId = departmentId;
			this.boundDepartmentRest = new IkeRestDepartment(this, departmentId);
		}

		return this.boundDepartmentRest;
	}

	action<T extends IkeAction>(action: T['action'], data?: T['request']): Promise<T['response']> {
		return this.core._post(action, data, this.accessToken, null);
	}

	bindAction<T extends IkeAction>(action: T['action']): (data?: T['request']) => Promise<T['response']> {
		return (data) => this.action<T>(action, data);
	}
}
